import React, { ReactElement, Ref, useCallback } from 'react';

import PropTypes from 'prop-types';

import { stripUnknownProps } from '@adsk/alloy-react-helpers';
import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { useTheme } from '@adsk/alloy-react-styles';
import {
  flattenStyle,
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import ToggleButton from './ToggleButton';
import getAriaProps from '../../helpers/getAriaProps';
import { GetToggleButtonProps, GenericItem, DataAttributes } from '../../types';

export type DropdownControlTokens = NonNullable<
  NonNullable<ReturnType<typeof useTheme>['tokens']>['component']['dropdown']
>['control'];

type ControlContainerSharedProps = DataAttributes & {
  getToggleButtonProps: GetToggleButtonProps;
  disabled?: boolean;
  isLoading?: boolean;
  error?: boolean;
  isOpen: boolean;
  highDensity?: boolean;
  highlightButtonOnFocus?: boolean;
  className?: string;
  style?: StylableComponent<HTMLDivElement>['style'];
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement | HTMLButtonElement>;
};

type ControlContainerSharedInternalProps = ControlContainerSharedProps & {
  ariaProps: ReturnType<typeof getAriaProps>;
  controlLabelProps: Record<string, unknown>;
  controlContainerStyles: ReturnType<typeof buildControlContainerStyles>;
  applyDefaultFullWidth: boolean;
};

/**
 * Props for `ControlContainer` when used as an autocomplete control.
 * The root element is always a `<div>`; keyboard activation is handled by the child `ToggleButton`.
 */
export type ControlContainerAutocompleteProps<_TItem extends GenericItem> =
  StylableComponent<HTMLDivElement> &
    ControlContainerSharedProps & {
      isAutocomplete: true;
    };

type ControlContainerDropdownBase<_TItem extends GenericItem> =
  ControlContainerSharedProps & {
    isAutocomplete?: false;
  };

/**
 * Props for `ControlContainer` when used as a standard dropdown control.
 * By default renders a `<button>` root; pass `useButtonRoot={false}` when the
 * control contains nested interactive children (for example token remove buttons).
 */
export type ControlContainerDropdownProps<TItem extends GenericItem> =
  StylableComponent<HTMLDivElement | HTMLButtonElement> &
    ControlContainerDropdownBase<TItem> & {
      /**
       * When false, keeps a div root for controls with nested interactive children (for example token remove buttons).
       * Defaults to true for standard single- and multi-select dropdown controls.
       *
       * With the default button root, width defaults to `100%` unless `style.width` is set.
       * Width via `className` alone (for example Tailwind `w-48`) does not opt out; use
       * `useButtonRoot={false}` or set `style.width`.
       */
      useButtonRoot?: boolean;
    };

export type ControlContainerProps<TItem extends GenericItem> =
  | ControlContainerAutocompleteProps<TItem>
  | ControlContainerDropdownProps<TItem>;

const buttonResetStyles = {
  display: 'flex',
  boxSizing: 'border-box' as const,
  margin: 0,
  padding: 0,
  border: 'none',
  background: 'inherit',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'left' as const,
};

const styleDefinesWidth = (
  style?: StylableComponent<HTMLElement>['style'],
): boolean => {
  const flat = flattenStyle(style);
  return flat?.width !== undefined && flat.width !== null && flat.width !== '';
};

export const buildControlContainerStyles = ({
  disabled,
  error,
  isOpen,
  style,
  controlTokens,
}: Pick<
  ControlContainerSharedProps,
  'disabled' | 'error' | 'isOpen' | 'style'
> & {
  controlTokens?: DropdownControlTokens;
}) => {
  const rootStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    outline: 'none',
    ...controlTokens?._,
    ...(isOpen && controlTokens?.focused),
    ...(disabled && controlTokens?.disabled),
    ...(error && !disabled && controlTokens?.error),
  };

  return [rootStyle, style] as [typeof rootStyle, typeof style];
};

const ControlChevron = ({
  disabled,
  highDensity,
  isOpen,
}: {
  disabled?: boolean;
  highDensity?: boolean;
  isOpen: boolean;
}) => {
  const { tokens } = useTheme();
  const iconTokens = tokens?.component.dropdown?.control?.icon;

  return (
    <div
      style={{
        height: 32,
        maxWidth: 36,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      data-testid={'Control__toggle'}
    >
      <ChevronDownIcon
        style={[
          {
            maxHeight: 32,
            maxWidth: 36,
            color: iconTokens?._?.color,
            marginLeft: highDensity ? 0 : iconTokens?._?.marginLeft,
            marginRight: iconTokens?._?.marginRight,
            ...(isOpen && {
              transform: 'rotate(180deg)',
            }),
            ...(disabled && {
              color: iconTokens?.disabled?.color,
            }),
          },
        ]}
      />
    </div>
  );
};

const ControlContainerBody = ({
  children,
  disabled,
  highDensity,
  highlightButtonOnFocus,
  isAutocomplete,
  isLoading,
  isOpen,
  getToggleButtonProps,
}: Pick<
  ControlContainerSharedProps,
  | 'children'
  | 'disabled'
  | 'highDensity'
  | 'highlightButtonOnFocus'
  | 'isLoading'
  | 'isOpen'
  | 'getToggleButtonProps'
> & {
  isAutocomplete: boolean;
}) => {
  const { tokens } = useTheme();
  const iconTokens = tokens?.component.dropdown?.control?.icon;

  return (
    <>
      {children}
      {isLoading && (
        <ProgressRing
          size={ProgressRing.SIZES.XSMALL}
          style={{
            marginLeft: highDensity ? 0 : iconTokens?._?.marginLeft,
          }}
        />
      )}
      {isAutocomplete ? (
        <ToggleButton
          isOpen={isOpen}
          disabled={disabled}
          getToggleButtonProps={getToggleButtonProps}
          tabIndex={-1}
          style={{
            marginLeft: highDensity ? 0 : iconTokens?._?.marginLeft,
            marginRight: iconTokens?._?.marginRight,
            ...(disabled && { color: iconTokens?.disabled?.color }),
          }}
          highlightButtonOnFocus={highlightButtonOnFocus}
        />
      ) : (
        <ControlChevron
          disabled={disabled}
          highDensity={highDensity}
          isOpen={isOpen}
        />
      )}
    </>
  );
};

const AutocompleteControlContainer = <TItem extends GenericItem>({
  className,
  controlContainerStyles,
  controlLabelProps,
  disabled = false,
  onClick,
  children,
  forwardedRef,
  getToggleButtonProps,
  isLoading,
  isOpen,
  highDensity,
  highlightButtonOnFocus,
}: ControlContainerSharedInternalProps &
  Pick<ControlContainerAutocompleteProps<TItem>, 'className'> & {
    forwardedRef: Ref<HTMLDivElement>;
  }) => {
  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled) {
        onClick?.(event);
      }
    },
    [disabled, onClick],
  );

  const handleOnMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const isInputElement = target.tagName === 'INPUT';
      const isButtonElement = target.tagName === 'BUTTON';

      if (!isInputElement && !isButtonElement) {
        event.preventDefault();

        const container = event.currentTarget;
        const inputElement = container.querySelector('input');

        if (inputElement) {
          inputElement.focus();
        }
      }
    },
    [],
  );

  return (
    // Autocomplete keeps a non-button container; the dedicated ToggleButton handles keyboard activation.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={forwardedRef}
      data-testid={'Dropdown__control'}
      aria-disabled={disabled ? true : undefined}
      className={className}
      css={controlContainerStyles}
      onClick={onClickHandler}
      onMouseDown={handleOnMouseDown}
      {...controlLabelProps}
    >
      <ControlContainerBody
        isAutocomplete
        disabled={disabled}
        getToggleButtonProps={getToggleButtonProps}
        highDensity={highDensity}
        highlightButtonOnFocus={highlightButtonOnFocus}
        isLoading={isLoading}
        isOpen={isOpen}
      >
        {children}
      </ControlContainerBody>
    </div>
  );
};

const ButtonRootControlContainer = ({
  className,
  controlContainerStyles,
  controlLabelProps,
  ariaProps,
  applyDefaultFullWidth,
  disabled = false,
  onClick,
  children,
  forwardedRef,
  getToggleButtonProps,
  isLoading,
  isOpen,
  highDensity,
  highlightButtonOnFocus,
}: ControlContainerSharedInternalProps & {
  className?: string;
  applyDefaultFullWidth: boolean;
  forwardedRef: Ref<HTMLButtonElement>;
}) => {
  const togglePropsFromDownshift = getToggleButtonProps({
    ref: forwardedRef,
    disabled,
    onClick,
    ...ariaProps,
  });

  return (
    <button
      type="button"
      data-testid={'Dropdown__control'}
      className={className}
      css={[
        buttonResetStyles,
        controlContainerStyles,
        applyDefaultFullWidth && { width: '100%' },
      ]}
      {...togglePropsFromDownshift}
      {...controlLabelProps}
      disabled={disabled}
      aria-disabled={disabled ? true : undefined}
    >
      <ControlContainerBody
        isAutocomplete={false}
        disabled={disabled}
        getToggleButtonProps={getToggleButtonProps}
        highDensity={highDensity}
        highlightButtonOnFocus={highlightButtonOnFocus}
        isLoading={isLoading}
        isOpen={isOpen}
      >
        {children}
      </ControlContainerBody>
    </button>
  );
};

const DivRootControlContainer = ({
  className,
  controlContainerStyles,
  controlLabelProps,
  ariaProps,
  disabled = false,
  onClick,
  children,
  forwardedRef,
  getToggleButtonProps,
  isLoading,
  isOpen,
  highDensity,
  highlightButtonOnFocus,
}: ControlContainerSharedInternalProps & {
  className?: string;
  forwardedRef: Ref<HTMLDivElement>;
}) => {
  const togglePropsFromDownshift = getToggleButtonProps({
    ref: forwardedRef as Ref<HTMLButtonElement>,
    disabled,
    onClick,
    ...ariaProps,
  });

  const { type: _toggleType, ...divToggleProps } = togglePropsFromDownshift;

  return (
    <div
      data-testid={'Dropdown__control'}
      className={className}
      css={controlContainerStyles}
      {...(divToggleProps as React.HTMLAttributes<HTMLDivElement>)}
      {...controlLabelProps}
    >
      <ControlContainerBody
        isAutocomplete={false}
        disabled={disabled}
        getToggleButtonProps={getToggleButtonProps}
        highDensity={highDensity}
        highlightButtonOnFocus={highlightButtonOnFocus}
        isLoading={isLoading}
        isOpen={isOpen}
      >
        {children}
      </ControlContainerBody>
    </div>
  );
};

const ControlContainerWrapped = <TItem extends GenericItem>(
  props: ControlContainerProps<TItem>,
  ref: Ref<HTMLDivElement | HTMLButtonElement>,
) => {
  const {
    className,
    style,
    disabled = false,
    isLoading = false,
    error,
    isOpen = false,
    getToggleButtonProps,
    isAutocomplete,
    children,
    highDensity,
    highlightButtonOnFocus,
    onClick,
    ...restProps
  } = props;

  const useButtonRoot =
    !isAutocomplete && 'useButtonRoot' in props
      ? props.useButtonRoot !== false
      : true;

  const { tokens } = useTheme();
  const controlTokens = tokens?.component.dropdown?.control;

  const ariaProps = getAriaProps(restProps);
  const ariaPropsKeys = Object.keys(ariaProps);

  const controlLabelProps = stripUnknownProps(restProps, [
    'selectItem',
    'selectedItem',
    'itemToString',
    'openMenu',
    'useButtonRoot',
    ...(isAutocomplete ? [] : ariaPropsKeys),
  ]);

  const controlContainerStyles = buildControlContainerStyles({
    disabled,
    error,
    isOpen,
    style,
    controlTokens,
  });

  const applyDefaultFullWidth = !styleDefinesWidth(style);

  const sharedProps = {
    getToggleButtonProps,
    isLoading,
    highDensity,
    highlightButtonOnFocus,
    ariaProps,
    disabled,
    isOpen,
    children,
    onClick,
    controlLabelProps,
    controlContainerStyles,
    applyDefaultFullWidth,
  };

  if (isAutocomplete) {
    return (
      <AutocompleteControlContainer
        {...sharedProps}
        className={className}
        forwardedRef={ref as Ref<HTMLDivElement>}
      />
    );
  }

  if (useButtonRoot) {
    return (
      <ButtonRootControlContainer
        {...sharedProps}
        className={className}
        forwardedRef={ref as Ref<HTMLButtonElement>}
      />
    );
  }

  return (
    <DivRootControlContainer
      {...sharedProps}
      className={className}
      forwardedRef={ref as Ref<HTMLDivElement>}
    />
  );
};

const ControlContainer = React.forwardRef(ControlContainerWrapped);

ControlContainer.displayName = 'ControlContainer';

ControlContainer.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to get props for the menu toggle button */
  getToggleButtonProps: PropTypes.func.isRequired,
  /** The content of te ControlContainer */
  children: PropTypes.any,
  /**
   * When false, keeps a div root for controls with nested interactive children (for example token remove buttons).
   * Defaults to true.
   */
  useButtonRoot: PropTypes.bool,
};

type ControlContainerRef<TProps extends ControlContainerProps<GenericItem>> =
  TProps extends { isAutocomplete: true }
    ? Ref<HTMLDivElement>
    : Ref<HTMLDivElement | HTMLButtonElement>;

export default Object.assign(
  ControlContainer as <TItem extends GenericItem>(
    p: ControlContainerProps<TItem> & {
      ref?: ControlContainerRef<ControlContainerProps<TItem>>;
    },
  ) => ReactElement,
  { propTypes: ControlContainer.propTypes },
);
